import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Main Layout Component to wrap your application pages
export const Layout = ({ children, pageTitle = 'Earn Media', onLogout }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();

  // Handle scroll detection for mobile/tablet floating pill UI transition
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleConfirmLogout = () => {
    // Delete the authToken cookie
    document.cookie = "authToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    
    // Call optional custom onLogout prop if passed
    if (onLogout) {
      onLogout();
    }

    setIsLogoutModalOpen(false);
    navigate('/login');
  };

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      path: '/',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      )
    },
    {
      id: 'offers',
      label: 'Offers',
      path: '/offers',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      id: 'reports',
      label: 'Reports',
      path: '/reports',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      )
    },
    {
      id: 'wallet',
      label: 'Wallet',
      path: '/wallet',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      id: 'profile',
      label: 'Profile',
      path: '/profile',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-zinc-950 font-sans text-zinc-100 flex flex-col antialiased relative">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Codystar:wght@400&family=Outfit:wght@800;900&display=swap');

        /* Slow, smooth, elegant sweep speed (8 seconds cycle) */
        @keyframes smoothShineSweep {
          0% {
            background-position: -200% 0;
          }
          100% {
            background-position: 200% 0;
          }
        }

        .dotted-title-animation {
          font-family: 'Codystar', cursive, sans-serif;
          font-weight: 400;
          letter-spacing: 0em;
          display: inline-block;
          white-space: nowrap;
          
          /* Solid high-contrast base with smooth green shine sweep */
          background: linear-gradient(
            110deg,
            #ffffff 0%,
            #ffffff 30%,
            #a7f3d0 45%,
            #10b981 50%,
            #6ee7b7 55%,
            #ffffff 70%,
            #ffffff 100%
          );
          background-size: 250% 100%;
          color: #ffffff;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          
          /* Sharpen dots so they stand out clearly on black without blurring */
          filter: drop-shadow(0 0 1px #ffffff) drop-shadow(0 0 6px rgba(16, 185, 129, 0.6));
          
          /* 8s linear infinite sweep animation */
          animation: smoothShineSweep 8s linear infinite;
        }

        .micro-interaction {
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .micro-interaction:hover {
          transform: translateY(-2px);
        }

        .micro-interaction:active {
          transform: translateY(0px) scale(0.98);
        }
      `}</style>

      {/* Top Navbar - Fixed Bar on Desktop, Floating Pill UI on Mobile/Tablet when scrolled */}
      <nav
        className={`fixed z-50 transition-all duration-300 ease-in-out flex items-center justify-between ${
          isScrolled
            ? 'top-3 left-4 right-4 h-16 rounded-full bg-zinc-900/90 backdrop-blur-md border border-zinc-700/60 px-4 shadow-2xl shadow-black/80 lg:top-0 lg:left-0 lg:right-0 lg:h-20 lg:rounded-none lg:bg-zinc-900 lg:border-b lg:border-t-0 lg:border-x-0 lg:border-zinc-800 lg:px-8 lg:shadow-none'
            : 'top-0 left-0 right-0 h-20 bg-zinc-900 border-b border-zinc-800 px-4 lg:px-8'
        }`}
      >
        <div className="flex items-center gap-3 min-w-0">
          {/* Mobile Sidebar Toggle Button */}
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="lg:hidden relative p-2.5 rounded-full bg-zinc-950 border border-zinc-800 text-zinc-300 hover:text-emerald-400 hover:border-emerald-500/40 hover:shadow-lg hover:shadow-emerald-500/10 micro-interaction group overflow-hidden flex-shrink-0"
            aria-label="Toggle Menu"
          >
            <div className="w-5 h-5 flex flex-col justify-between items-center transition-transform duration-300">
              <span className={`w-5 h-0.5 bg-current rounded-full transition-all duration-300 ${isSidebarOpen ? 'rotate-45 translate-y-2' : ''}`} />
              <span className={`w-3.5 h-0.5 bg-current rounded-full transition-all duration-300 ${isSidebarOpen ? 'opacity-0 translate-x-2' : 'group-hover:w-5'}`} />
              <span className={`w-5 h-0.5 bg-current rounded-full transition-all duration-300 ${isSidebarOpen ? '-rotate-45 -translate-y-2' : ''}`} />
            </div>
          </button>

          {/* Site Title with Dotted Codystar Font & Green Shiny Sweep */}
          <div className="flex items-center select-none overflow-hidden py-2 min-w-0">
            <h1 className="text-base sm:text-lg lg:text-2xl font-normal dotted-title-animation truncate">
              {pageTitle}
            </h1>
          </div>
        </div>

        {/* Logout Button */}
        <button
          onClick={() => setIsLogoutModalOpen(true)}
          className="group relative inline-flex items-center gap-2.5 px-4 py-2 lg:px-5 lg:py-2.5 rounded-full lg:rounded-2xl bg-zinc-950 border border-zinc-800 text-zinc-300 hover:text-white font-bold text-xs lg:text-sm overflow-hidden micro-interaction hover:border-emerald-500/50 hover:shadow-lg hover:shadow-emerald-500/10 flex-shrink-0"
        >
          <span className="absolute inset-0 bg-gradient-to-r from-emerald-600/20 via-teal-600/20 to-emerald-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          <svg
            className="w-4 h-4 text-zinc-400 group-hover:text-emerald-400 group-hover:translate-x-1 transition-all duration-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          <span className="relative z-10">Logout</span>
        </button>
      </nav>

      {/* Backdrop overlay for mobile sidebar only */}
      {isSidebarOpen && (
        <div
          onClick={() => setIsSidebarOpen(false)}
          className="fixed inset-0 top-0 z-30 bg-zinc-950/70 lg:hidden transition-opacity duration-300"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-20 left-0 z-40 w-72 h-[calc(100vh-5rem)] bg-zinc-900 border-r border-zinc-800 p-5 flex flex-col justify-between transition-transform duration-300 ease-in-out ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="space-y-6">
          <div className="px-3 py-2 text-xs font-bold uppercase tracking-wider text-zinc-500">
            Navigation Menu
          </div>

          <nav className="space-y-2">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path || (item.id === 'dashboard' && location.pathname === '/');
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    navigate(item.path);
                    setIsSidebarOpen(false);
                  }}
                  className={`w-full flex items-center gap-3.5 px-4 py-3.5 rounded-2xl text-sm font-bold micro-interaction relative overflow-hidden ${
                    isActive
                      ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 shadow-lg shadow-emerald-500/10'
                      : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/60 border border-transparent'
                  }`}
                >
                  {isActive && (
                    <span className="absolute left-0 top-2 bottom-2 w-1 bg-emerald-500 rounded-r-full shadow-[0_0_12px_#10b981]" />
                  )}
                  <span className={`transition-transform duration-300 ${isActive ? 'scale-110 text-emerald-400' : 'group-hover:scale-110'}`}>
                    {item.icon}
                  </span>
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Bottom Sidebar User Brief */}
        <div className="p-4 rounded-2xl bg-zinc-950 border border-zinc-800 flex items-center gap-3.5 micro-interaction">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-emerald-500 to-teal-600 flex items-center justify-center font-black text-zinc-950 text-sm shadow-md">
            EM
          </div>
          <div className="overflow-hidden">
            <h4 className="text-sm font-bold text-zinc-200 truncate">Partner Account</h4>
            <p className="text-xs text-zinc-500 truncate">active@earnmedia.com</p>
          </div>
        </div>
      </aside>

      {/* Main Workspace Area */}
      <main className="flex-1 min-h-[calc(100vh-5rem)] w-full pt-20 lg:pt-0 transition-all duration-300">
        <div className="w-full">
          {children}
        </div>
      </main>

      {/* Logout Confirmation Modal */}
      {isLogoutModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/80 backdrop-blur-sm p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-6 max-w-sm w-full shadow-2xl space-y-6">
            <div className="flex flex-col items-center text-center space-y-3">
              <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-zinc-100">Confirm Logout</h3>
              <p className="text-sm text-zinc-400">Are you sure you want to log out of your session?</p>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setIsLogoutModalOpen(false)}
                className="flex-1 px-4 py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-bold text-sm transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmLogout}
                className="flex-1 px-4 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-zinc-950 font-bold text-sm transition-colors shadow-lg shadow-emerald-500/20"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;